import React, { useMemo, useRef } from 'react';
import { ResponsiveContainer, ScatterChart, Scatter as ScatterPoint, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import Filters from './Filters';
import DateRangeFilter from './DateRangeFilter';
import LeadTimeChartTypeSelector from './LeadTimeChartTypeSelector';
import useIsMobile from '../hooks/useIsMobile';
import { getItemTypeColor, calculatePercentile, PERCENTILE_COLORS } from '../utils/chartUtils';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';

function ScatterPlot({ data, allTypes, visibleTypes, visiblePercentiles, setVisibleTypes, togglePercentile, leadTimeChartType, setLeadTimeChartType, plausible }) {
  const isMobile = useIsMobile();
  const chartRef = useRef(null);
  
  // Função para formatar data no formato brasileiro
  const formatDateBR = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };
  
  // Determina datas mín/max para o filtro
  const closedDates = useMemo(() => data.map(item => new Date(item['Closed Date'])).sort((a, b) => a - b), [data]);
  const minDate = closedDates.length ? closedDates[0].toISOString().slice(0, 10) : '';
  const maxDate = closedDates.length ? closedDates[closedDates.length - 1].toISOString().slice(0, 10) : '';
  const [startDate, setStartDate] = React.useState(minDate);
  const [endDate, setEndDate] = React.useState(maxDate);

  // Atualiza datas do filtro sempre que minDate/maxDate mudarem (ex: novo arquivo)
  React.useEffect(() => {
    setStartDate(minDate);
    setEndDate(maxDate);
  }, [minDate, maxDate]);

  // Filtra os dados conforme o período e tipos visíveis
  const filteredData = useMemo(() => data.filter(item => {
    const d = new Date(item['Closed Date']);
    const type = item['Tipo de Item'] || 'Item';
    return d >= new Date(startDate) && d <= new Date(endDate) && visibleTypes.includes(type);
  }), [data, startDate, endDate, visibleTypes]);
  const sortedData = useMemo(() => [...filteredData].sort((a, b) => new Date(a['Closed Date']) - new Date(b['Closed Date'])), [filteredData]);
  const scatterData = useMemo(() => sortedData.map(item => ({
    x: (item.closedDate instanceof Date ? item.closedDate : new Date(item.closedDate)).getTime(),
    y: item.leadTime,
    id: item.ID,
    type: item['Tipo de Item'] || 'Item',
    commitedDate: item['Committed Date'],
    closedDate: item['Closed Date'],
    color: getItemTypeColor(item['Tipo de Item'] || 'Item', allTypes)
  })), [sortedData, allTypes]);
  const groupedData = useMemo(() => scatterData.reduce((acc, item) => {
    const type = item.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {}), [scatterData]);
  const leadTimes = useMemo(() => sortedData.map(item => item.leadTime), [sortedData]);
  const percentiles = useMemo(() => ({
    P50: calculatePercentile(leadTimes, 50),
    P85: calculatePercentile(leadTimes, 85),
    P95: calculatePercentile(leadTimes, 95)
  }), [leadTimes]);
  let xTicks = [];
  if (sortedData.length > 0) {
    const uniqueMonths = Array.from(new Set(sortedData.map(item => {
      const d = new Date(item['Closed Date']);
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), 1).getTime();
    })));
    xTicks = uniqueMonths.sort((a, b) => a - b);
  }

  // Função de exportação de imagem
  const chartAreaRef = useRef(null);
  const handleExport = () => {
    if (!chartAreaRef.current) return;
    
    // Track the export action
    if (plausible) {
      plausible.trackExport('png', 'leadtime', 'leadtime-scatterplot');
      plausible.trackSection('export-png');
    }
    
    toPng(chartAreaRef.current, { backgroundColor: '#fff' })
      .then((dataUrl) => {
        saveAs(dataUrl, 'leadtime-scatterplot.png');
      })
      .catch(() => {
        alert('Erro ao exportar imagem');
      });
  };

  return (
    <div ref={chartRef} className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-1 sm:p-6 mt-8 mb-16 px-0 sm:px-2 flex flex-col items-stretch relative">
      <LeadTimeChartTypeSelector
        selectedType={leadTimeChartType}
        onTypeChange={setLeadTimeChartType}
        onExport={handleExport}
      />
      <div className="flex flex-col lg:flex-row justify-between items-start mb-2 sm:mb-4 gap-2">
        {/* Título removido - agora está no seletor */}
      </div>
      <div className="min-h-[320px] sm:min-h-[400px] max-h-[600px] sm:max-h-[700px] flex flex-col sm:flex-row">
        <div className="flex-1 flex flex-col min-w-0">
          <Filters
            allTypes={allTypes}
            visibleTypes={visibleTypes}
            setVisibleTypes={setVisibleTypes}
            visiblePercentiles={visiblePercentiles}
            togglePercentile={togglePercentile}
          />
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            minDate={minDate}
            maxDate={maxDate}
            className="mb-2"
          />
          <div className="flex flex-row w-full flex-1 min-h-0 items-stretch">
            <div className="flex-1 min-w-0 flex flex-col h-full">
              <div ref={chartAreaRef} style={{ background: '#fff', borderRadius: 8, padding: 0 }}>
                <ResponsiveContainer width="100%" minWidth={120} minHeight={320}>
                  <ScatterChart margin={{ top: 10, right: 10, left: 30, bottom: 10 }}>
                    <XAxis
                      type="number"
                      dataKey="x"
                      name="Data de Conclusão"
                      domain={xTicks.length > 0 ? [xTicks[0], xTicks[xTicks.length - 1]] : ['dataMin', 'dataMax']}
                      ticks={xTicks}
                      tickFormatter={date => {
                        const d = new Date(date);
                        // Usar formatação consistente com métodos UTC
                        const monthsShort = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 
                                           'jul', 'ago', 'set', 'out', 'nov', 'dez'];
                        const month = monthsShort[d.getUTCMonth()];
                        const year = d.getUTCFullYear().toString().slice(-2);
                        return `${month}/${year}`;
                      }}
                      scale="time"
                      interval={0}
                      axisLine={true}
                      tickLine={false}
                      tick={{ fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 500 }}
                      label={{
                        value: 'Data de Conclusão',
                        position: 'insideBottom',
                        offset: -5,
                        fontSize: isMobile ? 10 : 13,
                        fill: '#374151',
                        fontWeight: 600,
                      }}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      name="Lead Time"
                      axisLine={true}
                      tickLine={false}
                      width={55}
                      tick={{ fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 500 }}
                      label={{
                        value: 'Lead Time (dias)',
                        angle: -90,
                        position: 'left',
                        fontSize: isMobile ? 10 : 13,
                        fill: '#374151',
                        fontWeight: 600,
                        dx: 0,
                        dy: 0,
                      }}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-3 border rounded-lg shadow-lg">
                              <p className="font-semibold">ID: {data.id}</p>
                              <p style={{ color: data.color }}>Tipo: {data.type}</p>
                              <p>Lead Time: {data.y} dias</p>
                              <p>Committed: {formatDateBR(data.commitedDate)}</p>
                              <p>Closed: {formatDateBR(data.closedDate)}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {Object.entries(groupedData).map(([type, items]) => (
                      <ScatterPoint
                        key={type}
                        data={items}
                        fill={getItemTypeColor(type, allTypes)}
                        name={type}
                      />
                    ))}
                    {['P50', 'P85', 'P95'].map((key) => (
                      visiblePercentiles[key] && (
                        <ReferenceLine
                          key={key}
                          y={percentiles[key]}
                          stroke={PERCENTILE_COLORS[key]}
                          strokeDasharray="4 4"
                          strokeWidth={3}
                          label={{
                            value: `${key} ${Math.round(percentiles[key])} dias`,
                            position: 'insideTopRight',
                            fill: PERCENTILE_COLORS[key],
                            fontWeight: 700,
                            fontSize: isMobile ? 10 : 14,
                            dx: -10,
                            dy: 8,
                          }}
                        />
                      )
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScatterPlot;
